import { useCallback, useEffect, useMemo, useState } from 'react';

export interface UseChatFileAttachmentOptions {
  enabled?: boolean;
  allowedExtensions?: string[];
  maxSizeBytes?: number;
  validateFile?: (file: File) => string | null;
  onInvalidFile?: (file: File, reason: string) => void;
  onFileSelected?: (file: File) => void;
  onFileCleared?: () => void;
  formatFileSize?: (sizeInBytes: number) => string;
  labelPrefix?: string;
}

export interface ChatFileAttachmentState {
  selectedFile: File | null;
  selectFile: (file: File | null) => void;
  clearFile: () => void;
  attachmentLabel?: string;
}

const defaultFormatFileSize = (sizeInBytes: number) => `${(sizeInBytes / 1024).toFixed(1)}KB`;

const normalizeExtensions = (extensions: string[] | undefined) =>
  extensions?.map((extension) => extension.trim().toLowerCase().replace(/^[.]/, '')) ?? [];

export const useChatFileAttachment = ({
  enabled = true,
  allowedExtensions,
  maxSizeBytes,
  validateFile,
  onInvalidFile,
  onFileSelected,
  onFileCleared,
  formatFileSize = defaultFormatFileSize,
  labelPrefix = 'Attachment',
}: UseChatFileAttachmentOptions = {}): ChatFileAttachmentState => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  useEffect(() => {
    if (!enabled && selectedFile) {
      setSelectedFile(null);
      onFileCleared?.();
    }
  }, [enabled, onFileCleared, selectedFile]);

  const normalizedExtensions = useMemo(
    () => normalizeExtensions(allowedExtensions),
    [allowedExtensions]
  );

  const validateAgainstExtensions = useCallback(
    (file: File) => {
      if (!normalizedExtensions.length) {
        return true;
      }

      const fileExtension = file.name.split('.').pop()?.toLowerCase() ?? '';
      if (!normalizedExtensions.includes(fileExtension)) {
        const reason = `Unsupported file type: .${fileExtension || 'unknown'}`;
        onInvalidFile?.(file, reason);
        return false;
      }

      return true;
    },
    [normalizedExtensions, onInvalidFile]
  );

  const validateAgainstSize = useCallback(
    (file: File) => {
      if (typeof maxSizeBytes !== 'number') {
        return true;
      }

      if (file.size > maxSizeBytes) {
        const reason = `File is too large (max ${(maxSizeBytes / 1024).toFixed(0)}KB)`;
        onInvalidFile?.(file, reason);
        return false;
      }

      return true;
    },
    [maxSizeBytes, onInvalidFile]
  );

  const validateAgainstCustomRule = useCallback(
    (file: File) => {
      if (!validateFile) {
        return true;
      }

      const validationResult = validateFile(file);
      if (typeof validationResult === 'string' && validationResult.length > 0) {
        onInvalidFile?.(file, validationResult);
        return false;
      }

      return true;
    },
    [onInvalidFile, validateFile]
  );

  const selectFile = useCallback(
    (file: File | null) => {
      if (!enabled) {
        return;
      }

      if (!file) {
        setSelectedFile(null);
        onFileCleared?.();
        return;
      }

      const passesExtensionCheck = validateAgainstExtensions(file);
      if (!passesExtensionCheck) {
        return;
      }

      const passesSizeCheck = validateAgainstSize(file);
      if (!passesSizeCheck) {
        return;
      }

      const passesCustomCheck = validateAgainstCustomRule(file);
      if (!passesCustomCheck) {
        return;
      }

      setSelectedFile(file);
      onFileSelected?.(file);
    },
    [
      enabled,
      onFileCleared,
      onFileSelected,
      validateAgainstCustomRule,
      validateAgainstExtensions,
      validateAgainstSize,
    ]
  );

  const clearFile = useCallback(() => {
    if (!enabled || !selectedFile) {
      return;
    }

    setSelectedFile(null);
    onFileCleared?.();
  }, [enabled, onFileCleared, selectedFile]);

  const attachmentLabel = useMemo(() => {
    if (!enabled || !selectedFile) {
      return undefined;
    }

    return `${labelPrefix}: ${selectedFile.name} (${formatFileSize(selectedFile.size)})`;
  }, [enabled, formatFileSize, labelPrefix, selectedFile]);

  return {
    selectedFile: enabled ? selectedFile : null,
    selectFile,
    clearFile,
    attachmentLabel,
  };
};
